import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Realm } from '@/lib/api';
import { guardDestination, isInterstitial, resolveGuardState, useProfile } from './session';
import { Wordmark } from '@/domain/wordmark';
import { MicroLabel } from '@/ui/micro-label';
import { ErrorState } from '@/ui/error-state';

/**
 * The global route guard. Each of the six states has exactly one destination
 * (docs/30-interfaces/frontend.md section 3).
 *
 * Every intermediate screen (MFA setup/challenge, backup codes, onboarding) is reachable and
 * resolvable. Skipping one is what produces redirect loops; routing one to a stub is what strands
 * whoever lands on it, which is what onboarding did to every newly invited vendor.
 */
export function Guard({ realm, loginPath }: { realm: Realm; loginPath: string }) {
  const location = useLocation();
  const { data, isPending, isError, error, refetch } = useProfile(realm);

  if (isPending) return <GuardSplash />;
  // A failure to reach /me is not a signed-out session. Redirecting to login on a 500 hides the
  // outage and loses whatever the user was doing.
  if (isError)
    return (
      <div className="grid min-h-screen place-items-center bg-background p-6">
        <ErrorState
          error={error}
          onRetry={() => void refetch()}
          title="Could not check your session"
          className="w-full max-w-md"
        />
      </div>
    );

  const state = resolveGuardState(data ?? null);
  const base = realm === 'admin' ? '/admin' : '/v';

  // A cleared user is entitled to the whole tree except the screens that only exist to resolve
  // something: standing on one now means the last request just resolved it, and the profile this
  // guard re-read is what says so.
  if (state === 'cleared') return isInterstitial(location.pathname, realm) ? <Navigate to={base} replace /> : <Outlet />;

  const destination = guardDestination(state, realm, loginPath);
  // Already there: render it. This branch is the whole point of nesting the interstitials under the
  // guard -- each screen resolves its state by invalidating `/me` and re-rendering this component,
  // so the redirect that moves the user on can only come from here.
  if (location.pathname === destination) return <Outlet />;
  return <Navigate to={destination} replace state={{ from: location.pathname }} />;
}

/** Shown only while /me is in flight - deliberately quiet, no spinner. */
function GuardSplash() {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="grid justify-items-center gap-4 opacity-60">
        <Wordmark height={14} />
        <MicroLabel size="xs">Checking session</MicroLabel>
      </div>
    </div>
  );
}

/**
 * Keeps an authenticated user off the login screens. Without this, signing in and then pressing
 * Back lands on a login form that is already satisfied.
 */
export function RedirectIfSignedIn({ realm, to, children }: { realm: Realm; to: string; children: React.ReactNode }) {
  const { data, isPending } = useProfile(realm);
  if (isPending) return <GuardSplash />;
  // On error, fall through to the login form rather than blocking it: signing in is a reasonable
  // thing to let someone attempt when we could not determine whether they already are.
  if (data && resolveGuardState(data) === 'cleared') return <Navigate to={to} replace />;
  return <>{children}</>;
}
